import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ListPlus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SUB_TAB_LIST, SUB_TAB_TRIGGER } from "@/lib/subTabs";
import XeroLastRunPanel from "@/components/admin/XeroLastRunPanel";
import XeroPipelineRunCard from "@/components/admin/XeroPipelineRunCard";
import XeroPipelineTriggerCard from "@/components/admin/XeroPipelineTriggerCard";
import XeroQuotaTable from "@/components/admin/XeroQuotaTable";
import XeroRowCountsPanel from "@/components/admin/XeroRowCountsPanel";
import { XeroStatusError, XeroStatusLoading } from "@/components/admin/XeroStatusStates";
import XeroWatermarkPanel from "@/components/admin/XeroWatermarkPanel";
import {
  XERO_ORCHESTRATION_QUERY_KEY,
  XERO_PIPELINE_RUNS_QUERY_KEY,
  XERO_STATUS_QUERY_KEY,
  getXeroPipelineRun,
  getXeroStatus,
  isXeroRunActive,
  isXeroRunInFlight,
  listXeroPipelineRuns,
  xeroPipelineRunQueryKey,
  type XeroPipelineRun,
} from "@/services/xeroApi";

const POLL_MS = 5000;
const LIST_POLL_MS = 15000;

export default function XeroPipelineTab() {
  const queryClient = useQueryClient();
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [subTab, setSubTab] = useState("run");
  const [showQueueForm, setShowQueueForm] = useState(false);

  const status = useQuery({ queryKey: XERO_STATUS_QUERY_KEY, queryFn: getXeroStatus });

  const runs = useQuery({
    queryKey: XERO_PIPELINE_RUNS_QUERY_KEY,
    queryFn: () => listXeroPipelineRuns(10),
    refetchInterval: (query) =>
      query.state.data?.some((r) => isXeroRunActive(r.status)) ? LIST_POLL_MS : false,
  });

  const listed = runs.data ?? [];
  const defaultRun =
    listed.find((r) => isXeroRunInFlight(r.status)) ??
    listed.find((r) => r.status === "waiting") ??
    listed[0];
  const activeRunId = selectedRunId ?? defaultRun?.run_id ?? null;
  // A run waiting behind the one shown, if any.
  const queuedRun = listed.find((r) => r.status === "waiting" && r.run_id !== activeRunId);

  const run = useQuery({
    queryKey: activeRunId
      ? xeroPipelineRunQueryKey(activeRunId)
      : ["xero", "pipeline", "run", "none"],
    queryFn: () => getXeroPipelineRun(activeRunId as string),
    enabled: activeRunId !== null,
    refetchInterval: (query) =>
      isXeroRunActive(query.state.data?.status) ? POLL_MS : false,
  });

  const previousStatus = useRef<XeroPipelineRun["status"] | undefined>(undefined);
  const queuedRunRef = useRef(queuedRun);
  queuedRunRef.current = queuedRun;
  useEffect(() => {
    const current = run.data?.status;
    if (
      isXeroRunActive(previousStatus.current) &&
      current &&
      !isXeroRunActive(current)
    ) {
      queryClient.invalidateQueries({ queryKey: XERO_ORCHESTRATION_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: XERO_PIPELINE_RUNS_QUERY_KEY });
      const next = queuedRunRef.current;
      if (next) {
        queryClient.setQueryData(xeroPipelineRunQueryKey(next.run_id), next);
        setSelectedRunId(next.run_id);
      }
    }
    previousStatus.current = current;
  }, [run.data?.status, queryClient]);


  useEffect(() => {
    setShowQueueForm(false);
  }, [activeRunId]);

  const onTriggered = (triggered: XeroPipelineRun) => {
    // Seed the cache so the activity renders at once, before the first poll.
    queryClient.setQueryData(xeroPipelineRunQueryKey(triggered.run_id), triggered);
    setShowQueueForm(false);
    if (triggered.status === "waiting" && isXeroRunInFlight(run.data?.status)) {
      // Queued behind the run on screen: keep watching that one; the queued
      // run shows underneath it.
      queryClient.invalidateQueries({ queryKey: XERO_PIPELINE_RUNS_QUERY_KEY });
      return;
    }
    // Make sure the Run sub-tab is the one showing it.
    setSelectedRunId(triggered.run_id);
    setSubTab("run");
  };

  if (status.isLoading) {
    return <XeroStatusLoading />;
  }

  if (status.isError) {
    return (
      <XeroStatusError
        message={(status.error as Error)?.message}
        onRetry={() => status.refetch()}
      />
    );
  }

  const connected = status.data?.connected ?? false;
  const connections = status.data?.connections ?? [];
  // A queued run counts: the Run tab shows it (and polls it) until it has
  // started and finished, just like one already in Fabric.
  const inFlight = isXeroRunActive(run.data?.status);

  let disabledReason: string | null = null;
  if (!connected) {
    disabledReason = "Xero isn’t connected. Set it up on the Connection tab.";
  } else if (connections.length === 0) {
    disabledReason =
      "The consent granted no organisations. Reconnect and select at least one.";
  }

  return (
    <Tabs value={subTab} onValueChange={setSubTab}>
      <TabsList className={SUB_TAB_LIST}>
        <TabsTrigger value="run" className={SUB_TAB_TRIGGER}>
          Run
          {/* The spinner is why this label is not just static text: a backfill
              runs for a while and the operator will be on another sub-tab. */}
          {inFlight && <Loader2 className="ml-2 h-3.5 w-3.5 animate-spin" />}
        </TabsTrigger>
        <TabsTrigger value="last" className={SUB_TAB_TRIGGER}>
          Last run
        </TabsTrigger>
        <TabsTrigger value="budget" className={SUB_TAB_TRIGGER}>
          Request budget
        </TabsTrigger>
        <TabsTrigger value="watermarks" className={SUB_TAB_TRIGGER}>
          Watermarks
        </TabsTrigger>
        <TabsTrigger value="rowcounts" className={SUB_TAB_TRIGGER}>
          Row counts
        </TabsTrigger>
      </TabsList>

      <TabsContent value="run" className="mt-0 focus-visible:outline-none">
        {inFlight && run.data ? (
          <div className="space-y-4">
            <XeroPipelineRunCard
              run={run.data}
              isFetching={run.isFetching}
              onRefresh={() => run.refetch()}
            />
            {/* One run can wait behind this one: show it, or offer to queue. */}
            {queuedRun ? (
              <XeroPipelineRunCard
                run={queuedRun}
                isFetching={runs.isFetching}
                onRefresh={() => runs.refetch()}
              />
            ) : run.data.status !== "waiting" && showQueueForm ? (
              <XeroPipelineTriggerCard
                key={connections.map((c) => c.tenant_id).join(",")}
                connections={connections}
                disabledReason={disabledReason}
                onTriggered={onTriggered}
                queueOnly
              />
            ) : run.data.status !== "waiting" ? (
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => setShowQueueForm(true)}
                disabled={disabledReason !== null}
              >
                <ListPlus className="h-4 w-4" />
                Queue another run
              </Button>
            ) : null}
          </div>
        ) : (
          <XeroPipelineTriggerCard
            key={connections.map((c) => c.tenant_id).join(",")}
            connections={connections}
            disabledReason={disabledReason}
            onTriggered={onTriggered}
          />
        )}
      </TabsContent>

      <TabsContent value="last" className="mt-0 focus-visible:outline-none">
        {/* The run detail is already fetched and already reconciled here, so
            this costs no extra request. */}
        <XeroLastRunPanel
          run={run.data}
          isLoading={activeRunId !== null && run.isLoading}
          isFetching={run.isFetching}
          hasNoRuns={activeRunId === null && runs.isSuccess}
          onRefresh={() => run.refetch()}
        />
      </TabsContent>

      <TabsContent value="budget" className="mt-0 focus-visible:outline-none">
        <XeroQuotaTable />
      </TabsContent>

      <TabsContent value="watermarks" className="mt-0 focus-visible:outline-none">
        <XeroWatermarkPanel />
      </TabsContent>

      <TabsContent value="rowcounts" className="mt-0 focus-visible:outline-none">
        <XeroRowCountsPanel />
      </TabsContent>
    </Tabs>
  );
}
